import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	Button,
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import {
	useCreateService,
	usePatchService,
	useToggleServiceStatus,
} from '@/services/mutations/service';

interface ResourceConfigModalProps {
	isOpen: boolean;
	onClose: () => void;
	resource?: Services.Service | null;
}

const SlidingToggle = ({ options, value, onChange, label, disabled }: any) => {
	const foundIndex = options.findIndex((o: any) => o.value === value);
	const activeIndex = foundIndex === -1 ? 0 : foundIndex;

	const count = options.length;
	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400 block mb-2';

	return (
		<div className="w-full">
			{label && <label className={labelClass}>{label}</label>}
			<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
				<div
					className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
					style={{
						width: `calc((100% - 8px) / ${count})`,
						left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
					}}
				/>
				{options.map((opt: any) => (
					<button
						key={String(opt.value)}
						type="button"
						disabled={disabled}
						onClick={() => onChange(opt.value)}
						className={cn(
							'z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-[10px] font-black uppercase tracking-tight outline-none transition-colors duration-200 focus:outline-none',
							value === opt.value
								? 'text-stone-900'
								: 'text-stone-400 hover:text-stone-600',
						)}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	);
};

export const ResourceConfigModal = ({
	isOpen,
	onClose,
	resource,
}: ResourceConfigModalProps) => {
	const [formData, setFormData] = useState({
		service_name: '',
		value: '',
		value_currency: 'BRL',
		type: 'BENEFIT' as Services.ServiceType,
		origin: 'INTERNAL' as Services.Origin,
		// shelf: 'B2C' as Services.Shelf,
		professional_label: '',
		short_description: '',
		description: '',
		active: true,
		// Repasse único ao profissional por sessão (sem variação diurno/noturno).
		professional_share_value: '',
	});

	const [errors, setErrors] = useState<Record<string, string>>({});
	const [initialFormData, setInitialFormData] = useState<
		typeof formData | null
	>(null);

	const { mutateAsync: patchService, isPending: isPatching } =
		usePatchService(() => {
			onClose();
		});
	const { mutateAsync: createService, isPending: isCreating } =
		useCreateService(() => {
			onClose();
		});
	const { mutateAsync: toggleStatus, isPending: isToggling } =
		useToggleServiceStatus(() => {
			onClose();
		});
	const isPending = isPatching || isCreating || isToggling;

	useEffect(() => {
		if (isOpen) {
			setErrors({});
			if (resource) {
				// const shelfValue: Services.Shelf =
				//   resource.shelf === null ? 'B2C' : resource.shelf;
				const formattedValue = parseFloat(
					resource.value || '0',
				).toFixed(2);
				const formattedShare =
					resource.professional_share_value != null &&
					resource.professional_share_value !== ''
						? parseFloat(resource.professional_share_value).toFixed(
								2,
						  )
						: '';

				const loadedData = {
					service_name: resource.service_name || '',
					value: formattedValue,
					value_currency: resource.value_currency || 'BRL',
					type: resource.type || 'BENEFIT',
					origin: resource.origin || 'INTERNAL',
					// shelf: shelfValue,
					professional_label: resource.professional_label || '',
					short_description: resource.short_description || '',
					description: resource.description || '',
					active: resource.active ?? true,
					professional_share_value: formattedShare,
				};

				setFormData(loadedData);
				setInitialFormData(loadedData);
			} else {
				setFormData({
					service_name: '',
					value: '0.00',
					value_currency: 'BRL',
					type: 'BENEFIT',
					origin: 'INTERNAL',
					// shelf: 'B2C',
					professional_label: '',
					short_description: '',
					description: '',
					active: true,
					professional_share_value: '',
				});
				setInitialFormData(null);
			}
		}
	}, [isOpen, resource]);

	/** Normaliza entrada monetária (aceita vírgula/ponto, máx. 2 casas). Retorna null se inválido. */
	const normalizeMoneyInput = (input: string): string | null => {
		let rawValue = input.replace(/[^0-9,.]/g, '');
		rawValue = rawValue.replace(',', '.');

		const parts = rawValue.split('.');
		if (parts.length > 2) return null;

		let formattedValue = rawValue;
		if (parts.length === 2) {
			formattedValue = parts[0] + '.' + parts[1].substring(0, 2);
		}
		return formattedValue;
	};

	/** Completa casas decimais: "50" -> "50.00", "50.5" -> "50.50". */
	const finalizeMoney = (input: string): string => {
		let finalValue = input.replace(/[.,]$/, '');
		if (!finalValue.includes('.')) {
			finalValue += '.00';
		} else if (finalValue.split('.')[1].length === 1) {
			finalValue += '0';
		}
		return finalValue;
	};

	const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formattedValue = normalizeMoneyInput(e.target.value);
		if (formattedValue === null) return;
		setFormData({ ...formData, value: formattedValue });
	};

	const handleShareChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formattedValue = normalizeMoneyInput(e.target.value);
		if (formattedValue === null) return;
		setFormData({ ...formData, professional_share_value: formattedValue });
	};

	const validate = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.service_name.trim())
			newErrors.service_name = 'Obrigatório';

		if (!formData.description.trim()) newErrors.description = 'Obrigatório';

		const rawValueForValidation = formData.value.replace(/[.,]$/, '');
		if (
			!rawValueForValidation ||
			(parseFloat(rawValueForValidation) === 0 &&
				rawValueForValidation.length <= 1)
		) {
			newErrors.value = 'Obrigatório';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validate()) return;

		const finalValue = finalizeMoney(formData.value);

		// Repasse: vazio = sem repasse definido (null). Preenchido = "XX.XX".
		const rawShare = formData.professional_share_value.replace(/[.,]$/, '');
		const finalShare: string | null =
			rawShare === '' ? null : finalizeMoney(rawShare);

		const payload = {
			...formData,
			value: finalValue,
			professional_share_value: finalShare,
			professional_share_value_currency: finalShare
				? formData.value_currency
				: null,
		};

		try {
			if (resource?.id) {
				if (!initialFormData) {
					await patchService({ id: resource.id, body: payload });
					return;
				}

				const body: Partial<Services.Create> = {};
				const keys = [
					'service_name',
					'value',
					'value_currency',
					'type',
					'origin',
					'professional_label',
					'short_description',
					'description',
					'active',
					'professional_share_value',
				] as const;
				for (const k of keys) {
					const nextVal = payload[k];
					const prevVal =
						k === 'value'
							? initialFormData.value
							: k === 'professional_share_value'
							  ? initialFormData.professional_share_value === ''
									? null
									: initialFormData.professional_share_value
							  : initialFormData[k];
					if (nextVal !== prevVal) {
						(body as Record<string, unknown>)[k] = nextVal;
					}
				}
				// moeda do repasse acompanha o valor quando ele muda
				if ('professional_share_value' in body) {
					body.professional_share_value_currency =
						payload.professional_share_value_currency;
				}

				const isStatusOnlyChange =
					Object.keys(body).length === 1 && 'active' in body;

				if (isStatusOnlyChange) {
					await toggleStatus({
						id: resource.id,
						active: payload.active,
					});
				} else if (Object.keys(body).length > 0) {
					await patchService({ id: resource.id, body });
				} else {
					onClose();
				}
			} else {
				await createService(payload as Services.Create);
			}
		} catch (err) {
			const fallback = resource?.id
				? 'Erro ao atualizar o serviço.'
				: 'Erro ao criar o serviço.';
			const message = getErrorMessage(err, fallback);
			toast.error(message);
		}
	};

	if (!isOpen) return null;

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400';
	const inputClass =
		'w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none focus:border-stone-400 focus:bg-white transition-all box-border';

	const getFieldClass = (fieldName: string) =>
		cn(
			inputClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
				: 'border-stone-200 focus:border-stone-400 focus:bg-white',
		);

	const ErrorMsg = ({ field }: { field: string }) =>
		errors[field] ? (
			<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]}
			</span>
		) : null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<form
				onSubmit={handleSave}
				noValidate
				className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200"
			>
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
					<h2 className="text-lg font-black text-stone-900">
						{resource ? 'Editar Benefício' : 'Novo Serviço'}
					</h2>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 space-y-6 overflow-y-auto p-8">
					<div className="grid grid-cols-2 gap-4">
						<SlidingToggle
							label="Benefício Ativo?"
							value={formData.active}
							onChange={(val: boolean) =>
								setFormData({ ...formData, active: val })
							}
							options={[
								{ label: 'Sim', value: true },
								{ label: 'Não', value: false },
							]}
						/>
						<SlidingToggle
							label="Origem"
							value={formData.origin}
							onChange={(val: Services.Origin) =>
								setFormData({ ...formData, origin: val })
							}
							options={[
								{ label: 'Interno', value: 'INTERNAL' },
								{ label: 'Externo', value: 'EXTERNAL' },
							]}
						/>
					</div>

					{/* <SlidingToggle
            label="Distribuição (Shelf)"
            value={formData.shelf}
            onChange={(val: Services.Shelf) => {
              setFormData({
                ...formData,
                shelf: val,
              });
            }}
            options={[
              { label: 'B2C (Varejo)', value: 'B2C' },
              { label: 'B2B2C (Empresas)', value: 'B2B2C' },
            ]}
          /> */}

					<div className="space-y-1.5">
						<label className={labelClass}>NOME DO BENEFÍCIO</label>
						<input
							type="text"
							value={formData.service_name}
							onChange={(e) =>
								setFormData({
									...formData,
									service_name: e.target.value,
								})
							}
							className={getFieldClass('service_name')}
							placeholder="Ex: Telemedicina 24h"
						/>
						<ErrorMsg field="service_name" />
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<label className={labelClass}>
								TIPO DE SERVIÇO
							</label>
							<Select
								value={formData.type}
								onValueChange={(v) =>
									setFormData({
										...formData,
										type: v as Services.ServiceType,
									})
								}
							>
								<SelectTrigger className="h-[46px] w-full rounded-xl border-stone-200 bg-stone-50 px-4 font-bold text-stone-600 focus:border-stone-400 focus:ring-0">
									<SelectValue>
										{formData.type || 'Selecione o tipo'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="BENEFIT">
											BENEFIT
										</SelectItem>
										<SelectItem value="PROFESSION">
											PROFESSION
										</SelectItem>
										<SelectItem value="INFORMATION">
											INFORMATION
										</SelectItem>
										<SelectItem value="OTHER">
											OTHER
										</SelectItem>
										<SelectItem value="MOBILE_APP">
											MOBILE_APP
										</SelectItem>
									</SelectGroup>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>
								EXIBIÇÃO DO PROFISSIONAL
							</label>
							<input
								type="text"
								value={formData.professional_label}
								onChange={(e) =>
									setFormData({
										...formData,
										professional_label: e.target.value,
									})
								}
								className={inputClass}
								placeholder="Ex: Médico"
							/>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<label className={labelClass}>VALOR BASE</label>
							<input
								type="text"
								value={formData.value}
								onChange={handlePriceChange}
								className={getFieldClass('value')}
								placeholder="0.00"
							/>
							<ErrorMsg field="value" />
						</div>
						<div className="space-y-1.5">
							<label className={labelClass}>MOEDA</label>
							<Select
								value={formData.value_currency}
								onValueChange={(v) =>
									setFormData({
										...formData,
										value_currency: v,
									})
								}
							>
								<SelectTrigger className="h-[46px] w-full rounded-xl border-stone-200 bg-stone-50 px-4 font-bold text-stone-600 focus:border-stone-400 focus:ring-0">
									<SelectValue>
										{formData.value_currency === 'BRL'
											? 'BRL (R$)'
											: formData.value_currency}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="BRL">
											BRL (R$)
										</SelectItem>
									</SelectGroup>
								</SelectContent>
							</Select>
						</div>
					</div>

					<div className="space-y-1.5">
						<label className={labelClass}>
							REPASSE AO PROFISSIONAL (POR SESSÃO)
						</label>
						<input
							type="text"
							value={formData.professional_share_value}
							onChange={handleShareChange}
							className={inputClass}
							placeholder="Ex: 50.00 (deixe vazio se não houver repasse)"
						/>
						<p className="ml-1 text-[10px] leading-snug text-stone-400">
							Valor único pago ao profissional por sessão deste
							serviço. Aparece na tabela pública de remuneração
							(/profissional/repasse).
						</p>
					</div>

					<div className="space-y-1.5">
						<label className={labelClass}>DESCRIÇÃO CURTA</label>
						<input
							type="text"
							value={formData.short_description}
							onChange={(e) =>
								setFormData({
									...formData,
									short_description: e.target.value,
								})
							}
							className={inputClass}
							placeholder="Breve resumo do benefício (ex: Desconto de 50%)..."
						/>
					</div>

					<div className="space-y-1.5">
						<label className={labelClass}>
							DESCRIÇÃO DETALHADA
						</label>
						<textarea
							rows={3}
							value={formData.description}
							onChange={(e) =>
								setFormData({
									...formData,
									description: e.target.value,
								})
							}
							className={getFieldClass('description')}
							placeholder="Descreva as vantagens do benefício..."
						/>
						<ErrorMsg field="description" />
					</div>
				</div>

				<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						Cancelar
					</button>
					<Button
						type="submit"
						disabled={isPending}
						text={
							isPending
								? 'Salvando...'
								: resource
								  ? 'Salvar Alterações'
								  : 'Criar Benefício'
						}
						className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
						startIcon={
							isPending ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Check className="size-4 stroke-[3]" />
							)
						}
					/>
				</div>
			</form>
		</div>,
		document.body,
	);
};
